import { useEffect, useRef } from "react";
import { SUB_STYLES } from "@/components/editor/SubtitlesPanel";
import { musicUrl } from "@/components/editor/stickers/MusicPanel";

const HYPE = /^(clutch|ace|gg|non|quoi|wow|oh|omg|what|no|yes|insane|kill|pentakill|let'?s|go|mort|nooon|ouais)\W*$/i;

const clampSub = (v, half) => +Math.min(1 - Math.max(0.05, half), Math.max(Math.max(0.05, half), v)).toFixed(3);

const useSubDrag = (box, setCfg) => (e) => {
  if (!box?.current || !setCfg) return;
  e.preventDefault();
  e.stopPropagation();
  const r = box.current.getBoundingClientRect();
  const el = e.currentTarget.getBoundingClientRect();
  const dx = e.clientX - (el.left + el.width / 2), dy = e.clientY - (el.top + el.height / 2);
  const hw = Math.min(0.5, el.width / 2 / r.width), hh = Math.min(0.5, el.height / 2 / r.height);
  const move = (ev) => setCfg((c) => ({ ...c, sub_pos: "custom", sub_x: clampSub((ev.clientX - dx - r.left) / r.width, hw), sub_y: clampSub((ev.clientY - dy - r.top) / r.height, hh) }));
  const up = () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
  window.addEventListener("pointermove", move);
  window.addEventListener("pointerup", up);
};

// [captions.py width ratio, measured libass/CSS glyph-height ratio of the font]
const SUB_SIZE = { tiktok: [0.115, 0.86], pop: [0.092, 0.588], gaming: [0.083, 0.541], hormozi: [0.09, 0.588], mrbeast: [0.085, 0.86],
  neon: [0.09, 0.573], minimal: [0.06, 0.554], ali: [0.058, 0.554] };
const subFontCqw = (id, aspect, scale) => {
  const [size, k] = SUB_SIZE[id] || SUB_SIZE.gaming;
  return +(size * (aspect === "9:16" ? 1 : 0.8) * Math.min(2, Math.max(0.5, scale)) * k * 100).toFixed(3);
};
const activeEm = (st, on, hype) => {
  if (st.per === 1) return hype ? 1.14 : 1;
  if (!on) return 1;
  if (st.progressive) return hype ? 1.21 : 1.08;
  return hype ? 1.16 : 1.02;
};

const useSubScale = (setCfg, scale) => (e) => {
  if (!setCfg) return;
  e.preventDefault();
  e.stopPropagation();
  const el = e.currentTarget.parentElement.getBoundingClientRect();
  const cx = el.left + el.width / 2, cy = el.top + el.height / 2;
  const d0 = Math.max(8, Math.hypot(e.clientX - cx, e.clientY - cy));
  const move = (ev) => setCfg((c) => ({ ...c, sub_scale: +Math.min(2, Math.max(0.5, scale * Math.hypot(ev.clientX - cx, ev.clientY - cy) / d0)).toFixed(2) }));
  const up = () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
  window.addEventListener("pointermove", move);
  window.addEventListener("pointerup", up);
};

export const SubsPreview = ({ cfg, range, current, box, setCfg, playing }) => {
  const key = `${range.start.toFixed(1)}-${range.end.toFixed(1)}`;
  const drag = useSubDrag(box, setCfg);
  const scale = cfg.sub_scale ?? 1;
  const resize = useSubScale(setCfg, scale);
  if (!cfg.subs) return null;
  const editable = !!setCfg && !playing;
  const st = SUB_STYLES.find((s) => s.id === cfg.sub_style) || SUB_STYLES[0];
  const custom = cfg.sub_pos === "custom" && cfg.sub_y != null;
  const y = custom ? cfg.sub_y : cfg.sub_pos === "auto" ? (cfg.fit === "split" ? (cfg.split_ratio ?? 0.35) : cfg.aspect === "9:16" ? 0.7 : 0.8) : { high: 0.24, middle: 0.5, low: 0.78 }[cfg.sub_pos] || 0.7;
  const x = custom ? cfg.sub_x ?? 0.5 : 0.5;
  const words = cfg.words_key === key ? cfg.words || [] : null;
  let group = [], active = -1, ghost = !words;
  if (words) {
    let i = -1;
    words.forEach((w, j) => { if (w.s <= current) i = j; });
    if (i < 0 || current >= words[i].e + 0.25) {
      if (!editable) return null;
      ghost = true;
      i = Math.max(0, i);
    }
    const per = st.per || (["minimal", "ali"].includes(st.id) ? 5 : 3);
    const g0 = Math.floor(i / per) * per;
    group = words.length ? words.slice(g0, g0 + per) : [{ w: "TES SOUS-TITRES" }];
    active = words.length ? i - g0 : 0;
  } else if (st.per) {
    group = [{ w: st.per === 1 ? "CLUTCH" : "TES SOUS-TITRES" }];
    active = 0;
  } else {
    group = [{ w: cfg.sub_style === "minimal" || cfg.sub_style === "ali" ? "tes sous-titres ici" : "TES SOUS-TITRES" }];
  }
  return (
    <div onPointerDown={editable ? drag : undefined} data-testid="subs-preview" data-pos={cfg.sub_pos}
      className={`group/subs absolute text-center leading-tight rounded-md ${editable ? "pointer-events-auto cursor-move touch-none outline-dashed outline-1 outline-[#3ad7ff]/30 hover:outline-[#3ad7ff] px-1" : "pointer-events-none"}`}
      style={{ left: `${x * 100}%`, top: `${y * 100}%`, transform: "translate(-50%, -50%)", width: "max-content", maxWidth: "92%", fontSize: `${subFontCqw(st.id, cfg.aspect, scale)}cqw` }}>
      <span style={st.css} className={ghost ? "opacity-60" : ""}>
        {group.map((w, j) => {
          const txt = st.upper ? w.w.toUpperCase() : w.w;
          const on = j === active, hype = HYPE.test(w.w) || /!$/.test(w.w) || (w.w.length > 1 && w.w === w.w.toUpperCase() && /\p{L}/u.test(w.w));
          if (st.per) {
            const long = w.w.replace(/\W/g, "").length >= 6;
            const col = st.per === 1 ? (hype ? st.emph : long ? st.hl : undefined) : on ? (hype ? st.emph : st.hl) : undefined;
            return <span key={`${j}-${w.s}`} className={on ? "cx-sub-pop" : ""} data-testid={on ? "subs-preview-active" : undefined}
              style={{ color: col, display: "inline-block", visibility: st.progressive && j > active ? "hidden" : "visible", margin: "0 0.12em", fontSize: `${activeEm(st, on, hype)}em`, "--rot": hype && st.per === 1 ? "-4deg" : "0deg" }}>{txt}</span>;
          }
          return <span key={j} style={{ color: on ? st.hl : undefined, display: "inline-block", fontSize: `${activeEm(st, on, hype)}em`, margin: "0 0.12em" }}>{txt}</span>;
        })}
      </span>
      {editable && (
        <button type="button" onPointerDown={resize} data-testid="subs-scale-handle" aria-label="resize subtitles"
          className="absolute -right-2.5 -bottom-2.5 w-5 h-5 rounded-full bg-[#3ad7ff] border-2 border-white shadow-[0_0_10px_rgba(58,215,255,.8)] cursor-nwse-resize touch-none opacity-80 group-hover/subs:opacity-100" />
      )}
      {editable && scale !== 1 && <span data-testid="subs-scale-value" className="absolute -top-5 right-0 rounded bg-black/70 px-1 text-[9px] font-mono text-[#3ad7ff]">{Math.round(scale * 100)}%</span>}
    </div>
  );
};

export const useMusicSync = ({ cfg, vref, rel, inSel, playing, range, drops }) => {
  const audio = useRef(null);
  const m = cfg.music;
  useEffect(() => {
    audio.current?.pause();
    audio.current = m?.id ? new Audio(musicUrl(m.id)) : null;
    if (audio.current) audio.current.loop = true;
    return () => audio.current?.pause();
  }, [m?.id]);
  useEffect(() => {
    const v = vref.current, a = audio.current;
    if (v) v.volume = m ? Math.min(1, m.orig_vol ?? 0.6) : 1;
    if (!a) return;
    const len = range.end - range.start, fi = m.fade_in ?? 0.3, fo = m.fade_out ?? 1.2;
    const k = Math.min(1, fi > 0 ? rel / fi : 1, fo > 0 ? Math.max(0, len - rel) / fo : 1);
    a.volume = Math.max(0, Math.min(1, (m.vol ?? 0.8) * k));
    if (!playing || !inSel) { a.pause(); return; }
    const imp = cfg.impact ?? len / 3;
    const off = (m.sync ?? true) ? (drops[m.id] || 0) - Math.max(0, imp + (m.shift || 0)) : (m.start || 0);
    const want = rel + off;
    if (want < 0) { a.pause(); return; }
    if (Math.abs(a.currentTime - want) > 0.35) a.currentTime = want;
    if (a.paused) a.play().catch(() => {});
  }, [rel, playing, inSel, m, cfg.impact, range, drops, vref]);
};
